/**
 * Phase 5 — offline write queue.
 *
 * Queued writes (kiosk check-ins, field actions, upload metadata) are persisted
 * to native filesystem / localStorage and flushed automatically when the device
 * comes back online.
 *
 * A queued job is described by a *kind* plus a JSON payload. Handlers are
 * registered by kind at boot, so the queue file never holds functions and
 * survives an app restart.
 */
import { readJson, writeJson } from './storage';
import { isOnline } from './network';

const QUEUE_KEY = 'write-queue';
const MAX_ATTEMPTS = 500;

export interface QueuedWrite {
  id: string;
  kind: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

type Handler = (payload: unknown) => Promise<void>;

const handlers = new Map<string, Handler>();
const listeners = new Set<(count: number) => void>();
let flushing = false;

export function registerWriteHandler(kind: string, handler: Handler) {
  handlers.set(kind, handler);
}

export function onQueueChange(cb: (count: number) => void): () => void {
  listeners.add(cb);
  void getQueue().then((q) => cb(q.length));
  return () => listeners.delete(cb);
}

async function getQueue(): Promise<QueuedWrite[]> {
  return (await readJson<QueuedWrite[]>(QUEUE_KEY)) ?? [];
}

async function setQueue(queue: QueuedWrite[]) {
  await writeJson(QUEUE_KEY, queue);
  listeners.forEach((cb) => cb(queue.length));
}

export async function pendingWriteCount(): Promise<number> {
  return (await getQueue()).length;
}

/** Add a write to the queue. Returns the queued id. */
export async function enqueueWrite(kind: string, payload: unknown): Promise<string> {
  const job: QueuedWrite = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    kind,
    payload,
    createdAt: Date.now(),
    attempts: 0,
  };
  const queue = await getQueue();
  queue.push(job);
  await setQueue(queue);
  return job.id;
}

/**
 * Try a write now; if it fails because we're offline (or the call throws),
 * queue it for the next reconnect. Returns how the write was handled.
 */
export async function writeOrQueue(
  kind: string,
  payload: unknown,
): Promise<{ status: 'sent' } | { status: 'queued'; id: string }> {
  const handler = handlers.get(kind);
  if (handler && (await isOnline())) {
    try {
      await handler(payload);
      return { status: 'sent' };
    } catch {
      /* fall through to queue */
    }
  }
  const id = await enqueueWrite(kind, payload);
  return { status: 'queued', id };
}

/** Flush every queued write that has a registered handler. */
export async function flushWriteQueue(): Promise<{ sent: number; remaining: number }> {
  if (flushing) return { sent: 0, remaining: (await getQueue()).length };
  flushing = true;
  let sent = 0;
  try {
    if (!(await isOnline())) return { sent: 0, remaining: (await getQueue()).length };

    let queue = await getQueue();
    const keep: QueuedWrite[] = [];

    for (const job of queue) {
      const handler = handlers.get(job.kind);
      if (!handler) {
        keep.push(job);
        continue;
      }
      try {
        await handler(job.payload);
        sent += 1;
      } catch (e) {
        const attempts = job.attempts + 1;
        if (attempts < MAX_ATTEMPTS) {
          keep.push({
            ...job,
            attempts,
            lastError: e instanceof Error ? e.message : 'unknown_error',
          });
        } else {
          console.warn('[offline-queue] dropping job after max attempts', job.kind);
        }
      }
    }

    queue = keep;
    await setQueue(queue);
    return { sent, remaining: queue.length };
  } finally {
    flushing = false;
  }
}
